/**
 * SessionTimeoutManager - Centralized session timeout management
 * Prevents memory leaks by managing timers in a single place
 */

type Callback = () => void;

class SessionTimeoutManager {
  private warningTimeoutId: number | null = null;
  private expiryTimeoutId: number | null = null;

  /**
   * Initialize timers based on token expiration
   * @param params Configuration for session timeout
   */
  init(params: {
    tokenExp: number;         // JWT exp (in seconds)
    warningSeconds: number;   // e.g. 60
    onWarning: Callback;
    onExpired: Callback;
  }) {
    const { tokenExp, warningSeconds, onWarning, onExpired } = params;
    
    // Clear any existing timers first to prevent leaks
    this.clear();

    const nowMs = Date.now();
    const expMs = tokenExp * 1000;
    const msUntilExpiry = expMs - nowMs;
    const msUntilWarning = msUntilExpiry - warningSeconds * 1000;

    if (msUntilExpiry <= 0) {
      // Already expired
      onExpired();
      return;
    }

    if (msUntilWarning <= 0) {
      // Expiry is in less than warningSeconds → show modal immediately
      onWarning();
    } else {
      this.warningTimeoutId = window.setTimeout(onWarning, msUntilWarning);
    }

    this.expiryTimeoutId = window.setTimeout(onExpired, msUntilExpiry);
  }

  /**
   * Update timers when a new token is received (after refresh/continue)
   * @param tokenExp New token expiration time
   * @param warningSeconds Warning time before expiry
   * @param onWarning Callback when warning should be shown
   * @param onExpired Callback when token expires
   */
  updateTokenExp(
    tokenExp: number,
    warningSeconds: number,
    onWarning: Callback,
    onExpired: Callback
  ) {
    this.init({ tokenExp, warningSeconds, onWarning, onExpired });
  }

  /**
   * Clear all active timers
   * Call this on logout or component unmount
   */
  clear() {
    if (this.warningTimeoutId != null) {
      clearTimeout(this.warningTimeoutId);
      this.warningTimeoutId = null;
    }
    if (this.expiryTimeoutId != null) {
      clearTimeout(this.expiryTimeoutId);
      this.expiryTimeoutId = null;
    }
  }
}

export const sessionTimeoutManager = new SessionTimeoutManager();

